export type DataAvailabilityState =
  | 'ready'
  | 'loading'
  | 'reconnecting'
  | 'offline'
  | 'clock-unavailable'
  | 'server-unavailable'

export interface DataAvailabilityInput {
  browserOnline: boolean
  realtimeRequired: boolean
  wsConnected: boolean
  wsStateReady: boolean
  wsEverReady: boolean
  backendStatus: 'SYNCING' | 'SYNCED' | 'ERROR'
}

/**
 * Décide si les informations peuvent être présentées comme actuelles.
 *
 * Une socket ouverte ne suffit pas : le serveur doit d'abord avoir envoyé le
 * STATE_INIT du canal courant. Cela ferme la courte fenêtre où l'interface
 * montrait encore les valeurs de l'ancienne connexion pendant la resynchro.
 */
export function dataAvailabilityState(input: DataAvailabilityInput): DataAvailabilityState {
  if (!input.browserOnline) return 'offline'

  if (input.realtimeRequired) {
    // Les écrans d'antenne dépendent aussi de l'heure backend. Une socket
    // fraîche avec une horloge invérifiable ferait malgré tout avancer de faux
    // décomptes et de fausses positions sur la timeline.
    if (input.wsConnected && input.wsStateReady && input.backendStatus === 'SYNCED') return 'ready'
    if (input.wsConnected && input.wsStateReady && input.backendStatus === 'ERROR') return 'clock-unavailable'
    if (input.wsConnected) return 'loading'
    return input.wsEverReady ? 'reconnecting' : 'loading'
  }

  if (input.backendStatus === 'SYNCED') return 'ready'
  if (input.backendStatus === 'ERROR') return 'server-unavailable'
  return 'loading'
}

/**
 * Après le premier état complet d'une chaîne, une panne transitoire ne doit
 * plus démonter l'espace de travail. Le contenu reste visible sous un bandeau
 * de fraîcheur ; un changement de chaîne reste bloquant afin de ne jamais
 * présenter les données de la chaîne précédente comme si elles étaient les
 * bonnes.
 */
export function canPresentWorkspace(
  availability: DataAvailabilityState,
  activeScope: string | null,
  lastReadyScope: string | null,
): boolean {
  return availability === 'ready' || (
    activeScope !== null && activeScope === lastReadyScope
  )
}
